import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { getCutoffPreview, revealRound, recutShortlist } from '@/lib/selections-api';
import type { CutoffRule } from '@littlebooker/dal';

interface RevealPanelProps {
  selectionId: string;
  roundId: string;
  targetShortlist: number;
  targetFinal: number;
  expectedVoters: number;
  /** 'reveal' = first disclosure (irreversible); 'recut' = re-run on a revealed round. */
  mode?: 'reveal' | 'recut';
  onRevealed: () => void;
}

const numInput = 'w-16 rounded-md border border-input bg-background px-2 py-1';

export function RevealPanel({
  selectionId,
  roundId,
  targetShortlist,
  targetFinal,
  expectedVoters,
  mode = 'reveal',
  onRevealed,
}: RevealPanelProps) {
  const { data: preview, isLoading } = useQuery({
    queryKey: ['cutoff-preview', roundId, targetShortlist],
    queryFn: () => getCutoffPreview(selectionId, roundId, targetShortlist),
  });

  const [ruleMode, setRuleMode] = useState<'threshold' | 'top'>('threshold');
  const [min, setMin] = useState(2);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  const topSize = preview?.options.find((o) => o.rule.type === 'top')?.size;
  const rule: CutoffRule =
    ruleMode === 'threshold' ? { type: 'threshold', min } : { type: 'top', k: targetShortlist };
  const selectedSize =
    ruleMode === 'threshold' ? preview?.thresholdSizes[min] ?? preview?.wildcardCount ?? 0 : topSize ?? 0;

  const submit = useMutation({
    mutationFn: () =>
      mode === 'recut'
        ? recutShortlist(selectionId, roundId, { rule })
        : revealRound(selectionId, roundId, { rule }),
    onSuccess: onRevealed,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed'),
  });

  if (isLoading || !preview) {
    return <p className="text-sm text-muted-foreground">Tallying nominations…</p>;
  }

  const isRecut = mode === 'recut';
  const delta = selectedSize - targetShortlist;
  const isEmpty = selectedSize === 0;
  const belowFinal = selectedSize > 0 && selectedSize < targetFinal;
  const lowTurnout = expectedVoters > 0 && preview.ballotCount < expectedVoters;

  return (
    <div className="space-y-3 rounded-md border p-3">
      <p className="text-sm font-medium">{isRecut ? 'Re-cut shortlist' : 'Reveal → shortlist'}</p>
      <p className="text-xs text-muted-foreground">
        {preview.ballotCount} ballots · {preview.nominatedCount} shows nominated ·{' '}
        {preview.wildcardCount} wildcards.
      </p>

      {lowTurnout && (
        <p className="text-sm text-amber-600 dark:text-amber-500">
          Only {preview.ballotCount}/{expectedVoters} ballots submitted — nomination counts may be thin.
        </p>
      )}

      {preview.maxCount > 0 && (
        <div className="text-xs text-muted-foreground">
          {Array.from({ length: preview.maxCount }, (_, i) => preview.maxCount - i).map((c) => (
            <span key={c} className="mr-3">
              {preview.distribution[c] ?? 0} with {c} nom{c > 1 ? 's' : ''}
            </span>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <label className="flex flex-wrap items-center gap-2 text-sm">
          <input type="radio" checked={ruleMode === 'threshold'} onChange={() => setRuleMode('threshold')} />
          At least
          <NumberInput
            min={1}
            max={preview.maxCount || 1}
            allowEmpty={false}
            value={min}
            onChange={(n) => setMin(n ?? 1)}
            className={numInput}
          />
          nominations →{' '}
          <span className="font-medium text-foreground">
            {(preview.thresholdSizes[min] ?? preview.wildcardCount)} shows
          </span>
        </label>
        <label className="flex flex-wrap items-center gap-2 text-sm">
          <input type="radio" checked={ruleMode === 'top'} onChange={() => setRuleMode('top')} />
          Top {targetShortlist} by nomination count →{' '}
          <span className="font-medium text-foreground">{topSize ?? '—'} shows</span>
        </label>
      </div>

      <p className="text-xs text-muted-foreground">
        Target shortlist {targetShortlist} · this cutoff gives {selectedSize} ({delta >= 0 ? '+' : ''}
        {delta}). Wildcards are always included. Top-K keeps every show tied at the cutoff, so it can
        land above K.
      </p>

      {isEmpty && (
        <p className="text-sm text-destructive">
          This cutoff produces an empty shortlist — lower the threshold or use Top-K.
        </p>
      )}
      {belowFinal && (
        <p className="text-sm text-amber-600 dark:text-amber-500">
          Shortlist ({selectedSize}) is smaller than your final target ({targetFinal}) — round 2 won't be
          able to produce {targetFinal}.
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {confirming ? (
        <div className="flex flex-wrap items-center gap-2">
          {!isRecut && <span className="text-sm text-destructive">Reveal discloses all ballots.</span>}
          <Button size="sm" onClick={() => submit.mutate()} disabled={submit.isPending || isEmpty}>
            {submit.isPending ? 'Working…' : isRecut ? 'Confirm re-cut' : 'Confirm reveal'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button size="sm" onClick={() => setConfirming(true)} disabled={isEmpty}>
          {isRecut ? 'Re-cut…' : 'Reveal…'}
        </Button>
      )}
    </div>
  );
}
